import type React from "react";
import {useMemo, useState} from "react";

import {renderData} from "~/util/renderData";

import ButtonSpinner from "../spinner/ButtonSpinner";

export type KEY<T> = keyof T | ((item: T) => unknown);

function getKeyValue<T>(obj: T, key: KEY<T>) {
  if (typeof key === "function") return key(obj);
  return obj[key];
}

function makeCompareFn<T>(sortConfig: {
  key: KEY<T>;
  direction: "asc" | "desc";
}) {
  return (a: T, b: T) => {
    const valA = getKeyValue(a, sortConfig.key) as number | string | Date;
    const valB = getKeyValue(b, sortConfig.key) as number | string | Date;
    if (valA < valB) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (valA > valB) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  };
}

interface SortableTableProps<T> {
  data: T[];
  columns: {
    key: KEY<T>;
    label: string | React.ReactNode;
    sortable?: boolean;
    hiddenOnSmallScreens?: boolean;
    transformer?: (value: any, index: number) => React.ReactNode;
  }[];
  actions: {
    label: React.ReactNode | ((item: T) => React.ReactNode);
    action: (item: T) => void;
    className?: string;
    isLoading?: (item: T) => boolean;
  }[];
  defaultSorting?: {
    key: KEY<T>;
    direction: "asc" | "desc";
  };
}

const SortableTable = <T extends {[key: string]: unknown}>({
  data,
  columns,
  actions,
  defaultSorting,
}: SortableTableProps<T>) => {
  const [sortConfig, setSortConfig] = useState<{
    key: KEY<T>;
    direction: "asc" | "desc";
  }>(
    defaultSorting ?? {
      key: columns[0]!.key,
      direction: "asc",
    },
  );

  const sortedData = useMemo(() => {
    return [...data].sort(makeCompareFn(sortConfig));
  }, [sortConfig, data]);

  const handleSort = (key: KEY<T>) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({key, direction});
  };

  const getSortIndicator = (key: KEY<T>) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "↑" : "↓";
    }
    return " ";
  };

  return (
    <div className="overflow-x-auto pointer-events-auto">
      <div className="hidden sm:block">
        <table className="min-w-full rounded-lg">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key as string}
                  className={`py-2 px-4 text-left cursor-pointer ${column.hiddenOnSmallScreens ? "hidden lg:table-cell" : ""}`}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={typeof column.label === "string" ? column.label : ""}
                >
                  <div className="flex items-center">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <span className="ml-2 inline-block w-4">
                        {getSortIndicator(column.key)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              <th className="py-2 px-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td
                    key={column.key as string}
                    className={`py-2 px-4 ${column.hiddenOnSmallScreens ? "hidden lg:table-cell" : ""}`}
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={String(getKeyValue(item, column.key))}
                  >
                    {column.transformer
                      ? column.transformer(getKeyValue(item, column.key), index)
                      : renderData(getKeyValue(item, column.key))}
                  </td>
                ))}
                <td className="py-2 px-4">
                  <div className="flex gap-1">
                    {actions.map((action, idx) => (
                      <button
                        key={idx}
                        className={action.className}
                        onClick={() => action.action(item)}
                        disabled={action.isLoading?.(item) ?? false}
                      >
                        <ButtonSpinner
                          isLoading={action.isLoading?.(item) ?? false}
                        />
                        {typeof action.label === "function"
                          ? action.label(item)
                          : action.label}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="block sm:hidden">
        {sortedData.map((item, index) => (
          <div key={index} className="rounded-lg mb-4 p-4">
            {columns.map((column) => (
              <div
                key={column.key as string}
                className="flex justify-between mb-2"
              >
                <span>{column.label}</span>
                <span
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={String(getKeyValue(item, column.key))}
                >
                  {column.transformer
                    ? column.transformer(getKeyValue(item, column.key), index)
                    : renderData(getKeyValue(item, column.key))}
                </span>
              </div>
            ))}
            <div className="flex justify-between mt-4">
              {actions.map((action, idx) => (
                <button
                  key={idx}
                  className={action.className}
                  onClick={() => action.action(item)}
                  disabled={action.isLoading?.(item) ?? false}
                >
                  <ButtonSpinner
                    isLoading={action.isLoading?.(item) ?? false}
                  />
                  {typeof action.label === "function"
                    ? action.label(item)
                    : action.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SortableTable;
